/**
 * App-level share management (task #65 P1).
 *
 * Lists every share link for the app across releases with stats, lets
 * admins create (with optional password/expiry), copy the URL, and revoke
 * them. Shares have no default expiry — they live until revoked; an expiry
 * is opt-in and only shown when set. Legacy shares created before tokens
 * were stored have no recoverable URL.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Badge,
  Button, Input, Select, SelectTrigger, SelectValue, SelectIcon, SelectList, SelectContent, SelectItem, Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter, EmptyState, EmptyStateContent, EmptyStateIcon, EmptyStateTitle, EmptyStateDescription, Skeleton } from "raft-ui";
import { Share2 } from "lucide-react";
import type { BadgeProps } from "raft-ui";

type BadgeVariant = NonNullable<BadgeProps["variant"]>;
import {
  AppShare,
  createReleaseShare,
  listAppShares,
  listReleases,
  getRelease,
  rebindReleaseShare,
  renewReleaseShare,
  revokeReleaseShare,
} from "../lib/api";
import { useToast } from "../components/Toast";

export function AppShares({ appId }: { appId: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [rebindShare, setRebindShare] = useState<AppShare | null>(null);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);

  const shares = useQuery({
    queryKey: ["app-shares", appId],
    queryFn: () => listAppShares(appId),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["app-shares", appId] });

  const setExpiry = useMutation({
    mutationFn: ({ share, ttlDays }: { share: AppShare; ttlDays: number | null }) =>
      renewReleaseShare(appId, share.release_id, share.id, {
        // Explicit null clears the expiry (never expires).
        ...(ttlDays === null ? { expires_at: null } : { ttl_seconds: ttlDays * 24 * 60 * 60 }),
      }),
    onSuccess: (_data, vars) => {
      toast.show({
        kind: "success",
        title: vars.ttlDays === null ? "Expiry removed — link lives until revoked" : `Expires in ${vars.ttlDays} days`,
      });
      invalidate();
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Expiry update failed",
        description: (e as Error).message,
      }),
  });

  const setPassword = useMutation({
    mutationFn: ({ share, password }: { share: AppShare; password: string | null }) =>
      renewReleaseShare(appId, share.release_id, share.id, {
        // Omitting expiry keeps the share's current expiry unchanged.
        password,
      }),
    onSuccess: (_data, vars) => {
      toast.show({
        kind: "success",
        title: vars.password ? "Password set" : "Password removed",
      });
      invalidate();
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Password update failed",
        description: (e as Error).message,
      }),
  });

  const revoke = useMutation({
    mutationFn: (share: AppShare) =>
      revokeReleaseShare(appId, share.release_id, share.id),
    onSuccess: () => {
      toast.show({ kind: "success", title: "Share revoked" });
      invalidate();
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Revoke failed",
        description: (e as Error).message,
      }),
  });

  const rows = shares.data?.shares ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold">Shares</h2>
          <p className="text-sm text-foreground-muted">
            Public download pages for this app's releases. Links live until
            revoked and their URLs can be re-copied here anytime.
          </p>
        </div>
        <Button variant="primary" className="text-sm" onClick={() => setShowCreate(true)}>
          New share
        </Button>
      </div>

      {createdUrl && (
        <div className="flex flex-row items-center gap-3 rounded-md bg-fill-muted p-3 text-sm">
          <span className="font-mono break-all flex-1">{createdUrl}</span>
          <Button
            variant="outline"
            className="text-xs"
            onClick={() => {
              navigator.clipboard?.writeText(createdUrl);
              toast.show({ kind: "success", title: "Copied share URL" });
            }}
          >
            Copy
          </Button>
          <Button variant="outline" className="text-xs" onClick={() => setCreatedUrl(null)}>
            Dismiss
          </Button>
        </div>
      )}

      <div className="overflow-x-auto">
        {shares.isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}
        {shares.error && (
          <p className="text-sm text-danger">
            Failed to load shares: {(shares.error as Error).message}
          </p>
        )}
        {!shares.isLoading && rows.length === 0 && (
          <EmptyState>
            <EmptyStateContent>
              <EmptyStateIcon>
                <Share2 aria-hidden="true" />
              </EmptyStateIcon>
              <EmptyStateTitle>No shares yet</EmptyStateTitle>
              <EmptyStateDescription>
                Create one here, from the CLI (<code>hands releases share</code>),
                or from the release workflow.
              </EmptyStateDescription>
            </EmptyStateContent>
          </EmptyState>
        )}
        {rows.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-foreground-muted border-b border-line-muted">
                <th className="py-2 pr-3">Release</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Created</th>
                <th className="py-2 pr-3">URL</th>
                <th className="py-2 pr-3">Views</th>
                <th className="py-2 pr-3">Downloads</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((share) => (
                <ShareRow
                  key={share.id}
                  share={share}
                  onCopyUrl={() => {
                    if (!share.share_url) return;
                    navigator.clipboard?.writeText(share.share_url);
                    toast.show({ kind: "success", title: "Copied share URL" });
                  }}
                  onSetExpiry={() => {
                    const next = window.prompt(
                      "Days until this link expires (leave empty for never):",
                      share.expires_at
                        ? String(Math.max(1, Math.ceil((share.expires_at - Date.now()) / 86_400_000)))
                        : "",
                    );
                    if (next === null) return;
                    const trimmed = next.trim();
                    if (!trimmed) {
                      setExpiry.mutate({ share, ttlDays: null });
                      return;
                    }
                    const days = Number(trimmed);
                    if (!Number.isFinite(days) || days <= 0) {
                      toast.show({ kind: "error", title: "Enter a positive number of days" });
                      return;
                    }
                    setExpiry.mutate({ share, ttlDays: days });
                  }}
                  onRevoke={() => revoke.mutate(share)}
                  onRebind={() => setRebindShare(share)}
                  onSetPassword={() => {
                    const next = window.prompt(
                      share.has_password
                        ? "New password (leave empty to remove the password):"
                        : "Password for this share:",
                    );
                    if (next === null) return;
                    setPassword.mutate({
                      share,
                      password: next.trim() ? next.trim() : null,
                    });
                  }}
                  busy={revoke.isPending || setPassword.isPending || setExpiry.isPending}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <CreateShareModal
          appId={appId}
          onClose={() => setShowCreate(false)}
          onCreated={(url) => {
            setShowCreate(false);
            setCreatedUrl(url);
            invalidate();
          }}
        />
      )}
      {rebindShare && (
        <RebindShareModal
          appId={appId}
          share={rebindShare}
          onClose={() => setRebindShare(null)}
          onRebound={() => {
            setRebindShare(null);
            invalidate();
          }}
        />
      )}
    </div>
  );
}

function shareState(share: AppShare): { label: string; variant: BadgeVariant } {
  if (share.revoked_at) return { label: "revoked", variant: "muted" };
  // Legacy shares may still carry an expiry; new ones live until revoked.
  if (share.expires_at != null && share.expires_at <= Date.now()) {
    return { label: "expired", variant: "warning" };
  }
  return { label: "active", variant: "success" };
}

function ShareRow({
  share,
  onCopyUrl,
  onSetExpiry,
  onRevoke,
  onRebind,
  onSetPassword,
  busy,
}: {
  share: AppShare;
  onCopyUrl: () => void;
  onSetExpiry: () => void;
  onRevoke: () => void;
  onRebind: () => void;
  onSetPassword: () => void;
  busy: boolean;
}) {
  const state = shareState(share);
  const actionable = !share.revoked_at;
  return (
    <tr className="border-b border-line-hairline last:border-0">
      <td className="py-2 pr-3">
        <span className="font-medium">
          {share.version_name} ({share.version_code})
        </span>
        <span className="ml-2 text-xs text-foreground-muted">{share.channel_slug}</span>
        <div className="text-xs text-foreground-hint">by {share.created_by}</div>
      </td>
      <td className="py-2 pr-3">
        <Badge variant={state.variant}>
          {state.label}
        </Badge>
        {Boolean(share.has_password) && (
          <Badge variant="information" className="ml-1">
            password
          </Badge>
        )}
        {share.expires_at != null && !share.revoked_at && share.expires_at > Date.now() && (
          <div className="mt-0.5 text-xs text-foreground-hint">
            expires {new Date(share.expires_at).toLocaleString()}
          </div>
        )}
      </td>
      <td className="py-2 pr-3 text-xs text-foreground">
        {new Date(share.created_at).toLocaleString()}
      </td>
      <td className="py-2 pr-3 text-xs">
        {share.share_url ? (
          <Button variant="link" size="sm" onClick={onCopyUrl}>
            Copy URL
          </Button>
        ) : (
          <span className="text-foreground-hint" title="Created before URLs were stored; the link itself still works.">
            unavailable (legacy)
          </span>
        )}
      </td>
      <td className="py-2 pr-3">
        {share.view_count}
        <span className="text-xs text-foreground-hint"> ({share.unique_view_count} uniq)</span>
      </td>
      <td className="py-2 pr-3">
        {share.download_count}
        <span className="text-xs text-foreground-hint"> ({share.unique_download_count} uniq)</span>
      </td>
      <td className="py-2 text-right text-xs whitespace-nowrap">
        {actionable && (
          <>
            <Button variant="link" size="sm" className="mr-2" onClick={onSetExpiry} disabled={busy}>
              Expiry…
            </Button>
            <Button variant="link" size="sm" className="mr-2" onClick={onSetPassword} disabled={busy}>
              {share.has_password ? "Password…" : "Set password"}
            </Button>
            <Button variant="link" size="sm" className="mr-2" onClick={onRebind} disabled={busy}>
              Rebind release…
            </Button>
            <Button variant="link" size="sm" className="text-danger" onClick={onRevoke} disabled={busy}>
              Revoke
            </Button>
          </>
        )}
      </td>
    </tr>
  );
}

function RebindShareModal({
  appId,
  share,
  onClose,
  onRebound,
}: {
  appId: string;
  share: AppShare;
  onClose: () => void;
  onRebound: () => void;
}) {
  const toast = useToast();
  const [targetReleaseId, setTargetReleaseId] = useState("");
  const releases = useQuery({
    queryKey: ["releases", appId],
    queryFn: () => listReleases(appId),
  });
  const options = useMemo(
    () => (releases.data?.releases ?? [])
      .filter((release) => release.status === "active"
        && release.id !== share.release_id
        && release.channel_id === share.channel_id),
    [releases.data, share.channel_id, share.release_id],
  );
  const target = useQuery({
    queryKey: ["release", appId, targetReleaseId],
    queryFn: () => getRelease(appId, targetReleaseId),
    enabled: Boolean(targetReleaseId),
  });
  const installable = target.data?.assets.find((asset) => asset.artifact_kind === "installable");
  const rebind = useMutation({
    mutationFn: () => rebindReleaseShare(appId, share.id, {
      expected_release_id: share.release_id,
      target_release_id: targetReleaseId,
    }),
    onSuccess: (data) => {
      toast.show({
        kind: "success",
        title: `Share now points to ${data.target.version_name} (${data.target.version_code})`,
      });
      onRebound();
    },
    onError: (error) => toast.show({
      kind: "error",
      title: "Release rebind failed",
      description: (error as Error).message,
    }),
  });

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Rebind share to another release</DialogTitle></DialogHeader>
        <DialogBody className="space-y-3">
          <p className="text-sm text-foreground">
            The public URL stays the same. Its version, build, download, and checksum will all point to the selected release.
          </p>
          <div className="rounded-md border border-line-muted p-3 text-sm">
            <div className="text-xs text-foreground-muted">Current release</div>
            <strong>{share.version_name} ({share.version_code})</strong>
            <span className="ml-2 text-xs text-foreground-muted">{share.channel_slug}</span>
          </div>
          <label className="block text-xs text-foreground">
            Target active release
            <Select
              items={{
                "": "Select an active release…",
                ...Object.fromEntries(options.map((release) => [
                  release.id,
                  `${release.version_name ?? release.id.slice(0, 8)} (${release.version_code ?? "—"})`,
                ])),
              }}
              value={targetReleaseId}
              onValueChange={(value) => setTargetReleaseId(value as string)}
            >
              <SelectTrigger className="mt-1 py-1.5!"><SelectValue /><SelectIcon /></SelectTrigger>
              <SelectContent>
                <SelectList>
                <SelectItem value="">Select an active release…</SelectItem>
                {options.map((release) => (
                  <SelectItem key={release.id} value={release.id}>
                    {release.version_name ?? release.id.slice(0, 8)} ({release.version_code ?? "—"})
                  </SelectItem>
                ))}
              </SelectList>
              </SelectContent>
            </Select>
          </label>
          {target.isLoading && <Skeleton className="h-20 w-full" />}
          {target.data && (
            <div className="rounded-md border border-warning bg-warning-soft p-3 text-sm">
              <div><strong>{target.data.build.version_name}</strong> ({target.data.build.version_code})</div>
              <div className="mt-1 text-xs text-foreground">Build: <code>{target.data.build.id}</code></div>
              <div className="mt-1 break-all text-xs text-foreground">Checksum: <code>{installable?.file_hash ?? "No installable asset"}</code></div>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!targetReleaseId || !installable || rebind.isPending}
            onClick={() => rebind.mutate()}
          >
            {rebind.isPending ? "Rebinding…" : "Rebind release"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateShareModal({
  appId,
  onClose,
  onCreated,
}: {
  appId: string;
  onClose: () => void;
  onCreated: (url: string) => void;
}) {
  const toast = useToast();
  const [releaseId, setReleaseId] = useState("");
  const [password, setPassword] = useState("");
  const [ttlDays, setTtlDays] = useState("");

  const releases = useQuery({
    queryKey: ["releases", appId],
    queryFn: () => listReleases(appId),
  });
  const options = useMemo(
    () =>
      (releases.data?.releases ?? [])
        .filter((r: any) => r.status === "active" || r.status === "superseded")
        .slice(0, 30),
    [releases.data],
  );

  const create = useMutation({
    mutationFn: () =>
      // No ttl (the default): the share lives until revoked.
      createReleaseShare(appId, releaseId, {
        ...(ttlDays.trim() ? { ttl_seconds: Number(ttlDays) * 24 * 60 * 60 } : {}),
        ...(password.trim() ? { password: password.trim() } : {}),
      }),
    onSuccess: (data) => {
      toast.show({
        kind: "success",
        title: data.has_password ? "Password-protected share created" : "Share created",
      });
      onCreated(data.share_url);
    },
    onError: (e) =>
      toast.show({
        kind: "error",
        title: "Create share failed",
        description: (e as Error).message,
      }),
  });

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New share link</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-3">
          <label className="block text-xs text-foreground">
            Release
            <Select
              items={{
                "": "Select a release…",
                ...Object.fromEntries(
                  options.map((r: any) => [r.id, `${r.version_name ?? r.id.slice(0, 8)} · ${r.status}`]),
                ),
              }}
              value={releaseId}
              onValueChange={(v) => setReleaseId(v as string)}
            >
              <SelectTrigger className="mt-1 py-1.5!">
                <SelectValue />
                <SelectIcon />
              </SelectTrigger>
              <SelectContent>
                <SelectList>
                <SelectItem value="">Select a release…</SelectItem>
                {options.map((r: any) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.version_name ?? r.id.slice(0, 8)} · {r.status}
                  </SelectItem>
                ))}
              </SelectList>
              </SelectContent>
            </Select>
          </label>
          <label className="block text-xs text-foreground">
            Expires in days (optional)
            <Input
              type="number"
              min={1}
              value={ttlDays}
              onChange={(e) => setTtlDays(e.target.value)}
              placeholder="Leave empty — link lives until revoked"
              className="mt-1 py-1.5!"
            />
          </label>
          <label className="block text-xs text-foreground">
            Password (optional)
            <Input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Leave empty for a public link"
              className="mt-1 py-1.5!"
            />
          </label>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" className="text-sm" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            className="text-sm"
            onClick={() => create.mutate()}
            disabled={create.isPending || !releaseId}
          >
            {create.isPending ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
